// Admin — Amenity Waiver Vouchers. Accessible to Super Admin, Editor and
// Infrastructure roles. Generate, browse, and revoke single-use waiver
// codes that fully waive an amenity booking's charges. Each voucher is
// valid for 7 days from creation.
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Ticket, Search, Copy, Ban, Plus, Info, ShieldCheck, ShieldX,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls = "bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const STATUS_BADGE = {
  ACTIVE: "bg-[#E7F3F1] text-[#0D5C63] border-[#0D5C63]/30",
  USED: "bg-[#F1ECE0] text-[#5B5B5B] border-[#DDD3BA]",
  EXPIRED: "bg-[#FEF7E4] text-[#8a5a1a] border-[#c78e42]/40",
  REVOKED: "bg-[#FDECEC] text-[#B02020] border-[#B02020]/30",
};

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; }
}
function fmtDT(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; }
}

export default function AmenityVouchers() {
  const { role } = useAuth();
  const canManage = ["super_admin", "editor", "infrastructure"].includes(role);
  const [rows, setRows] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("ALL");
  const [amenityFilter, setAmenityFilter] = useState("");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [revokeFor, setRevokeFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "ALL") params.status = status;
      if (amenityFilter) params.amenity_id = amenityFilter;
      const { data } = await api.get("/admin/amenity-vouchers", { params });
      setRows(data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load vouchers");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/amenities");
        setAmenities((data || []).filter((a) => a.status === "Active"));
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status, amenityFilter]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => [r.code, r.notes, r.amenity_title, r.created_by].some((x) => (x || "").toString().toLowerCase().includes(term)));
  }, [rows, q]);

  const revoke = async () => {
    if (!revokeFor) return;
    try {
      await api.post(`/admin/amenity-vouchers/${revokeFor.code}/revoke`);
      toast.success("Voucher revoked");
      setRevokeFor(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Revoke failed");
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-amenity-vouchers">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Infrastructure</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Amenity Waiver Vouchers</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">Generate single-use codes for amenity bookings: a 100% waiver (default), a percentage or a fixed-amount discount. The applicant pays any balance online. Every voucher is valid for 7 days from creation.</div>
        </div>
        {canManage && (
          <button type="button" onClick={() => setCreateOpen(true)} data-testid="av-new" className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm">
            <Plus size={16} /> Generate Voucher
          </button>
        )}
      </div>

      <div className="bg-white border border-[#e5e0d8] rounded-sm p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8a8478]" />
          <input className={`${inputCls} pl-8 w-full`} placeholder="Search code, notes, amenity, issuer…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="av-search" />
        </div>
        <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="av-status">
          {["ALL", "ACTIVE", "USED", "EXPIRED", "REVOKED"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={inputCls} value={amenityFilter} onChange={(e) => setAmenityFilter(e.target.value)} data-testid="av-amenity-filter">
          <option value="">All amenities</option>
          {amenities.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
        </select>
        <button type="button" onClick={load} className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-3 py-2 rounded-sm font-semibold" data-testid="av-refresh">Refresh</button>
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#e5e0d8] rounded-sm p-10 text-center">
          <Ticket size={36} className="mx-auto text-[#bcb6aa]" strokeWidth={1.2} />
          <div className="mt-3 text-sm text-[#6b655b]">No vouchers yet. Click <b>Generate Voucher</b> to create one.</div>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#f5f1e8] text-[#5b5650]">
              <tr>
                <Th>Code</Th><Th>Discount</Th><Th>Restricted to</Th><Th>Status</Th><Th>Created</Th>
                <Th>Expires</Th><Th>Notes</Th><Th>Used by</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.code} className="border-t border-[#e5e0d8]" data-testid={`av-row-${r.code}`}>
                  <td className="px-3 py-3 align-top">
                    <div className="font-mono text-[13px] font-semibold text-[#0D5C63]">{r.code}</div>
                    <button type="button" onClick={() => copy(r.code)} className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-[#8a8478] hover:text-[#0D5C63]"><Copy size={10} /> copy</button>
                  </td>
                  <td className="px-3 py-3 align-top text-xs font-semibold whitespace-nowrap" data-testid={`av-discount-${r.code}`}>{r.discount_label || (r.discount_type === "PERCENT" ? `${r.discount_value}% off` : r.discount_type === "FIXED" ? `Rs. ${Number(r.discount_value).toLocaleString("en-IN")} off` : "100% waiver")}</td>
                  <td className="px-3 py-3 align-top">{r.amenity_title || <span className="text-[#8a8478]">Any amenity</span>}</td>
                  <td className="px-3 py-3 align-top">
                    <span className={`inline-block text-[10px] uppercase tracking-widest font-bold border rounded px-2 py-0.5 ${STATUS_BADGE[r.status] || ""}`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-3 align-top text-xs">
                    <div>{fmtDT(r.created_at)}</div>
                    <div className="text-[10px] text-[#8a8478]">by {r.created_by || "—"}</div>
                  </td>
                  <td className="px-3 py-3 align-top text-xs">{fmtDate(r.expires_at)}</td>
                  <td className="px-3 py-3 align-top text-xs max-w-[220px]"><div className="line-clamp-3">{r.notes || "—"}</div></td>
                  <td className="px-3 py-3 align-top text-xs">
                    {r.used_by_booking_ref ? (
                      <>
                        <div className="font-mono">{r.used_by_booking_ref}</div>
                        <div className="text-[10px] text-[#8a8478]">{fmtDT(r.used_at)}</div>
                      </>
                    ) : <span className="text-[#8a8478]">—</span>}
                  </td>
                  <td className="px-3 py-3 align-top">
                    {canManage && ["ACTIVE", "EXPIRED"].includes(r.status) && (
                      <button type="button" onClick={() => setRevokeFor(r)} data-testid={`av-revoke-${r.code}`} className="text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1"><Ban size={11} /> Revoke</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateVoucherDialog open={createOpen} onClose={() => setCreateOpen(false)} amenities={amenities} onCreated={(res) => { setCreateOpen(false); setCreateResult(res); load(); }} />

      <Dialog open={!!createResult} onOpenChange={(o) => !o && setCreateResult(null)}>
        <DialogContent className="bg-[#fbf8f1] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2"><ShieldCheck size={22} className="text-[#0D5C63]" /> Vouchers Ready</DialogTitle>
            <DialogDescription>Copy and share these codes with recipients. Each is valid for 7 days and can be used once.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 max-h-[50vh] overflow-y-auto">
            {(createResult?.vouchers || []).map((v) => (
              <div key={v.code} className="flex items-center justify-between gap-2 bg-white border border-[#e5e0d8] rounded p-3">
                <div>
                  <div className="font-mono font-semibold text-[#0D5C63] text-sm">{v.code}</div>
                  <div className="text-[10px] text-[#8a8478]">Valid till {fmtDate(v.expires_at)}</div>
                </div>
                <button type="button" onClick={() => copy(v.code)} className="text-xs font-semibold text-[#0D5C63] hover:bg-[#E7F3F1] px-2 py-1 rounded inline-flex items-center gap-1"><Copy size={12} /> Copy</button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setCreateResult(null)} className="text-sm px-4 py-2.5 rounded-sm bg-[#234333] text-white font-semibold">Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!revokeFor} onOpenChange={(o) => !o && setRevokeFor(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl inline-flex items-center gap-2"><ShieldX size={22} /> Revoke voucher?</AlertDialogTitle>
            <AlertDialogDescription>
              Voucher <b className="font-mono">{revokeFor?.code}</b> will be permanently revoked and cannot be used any more. Bookings already confirmed with this voucher are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep active</AlertDialogCancel>
            <AlertDialogAction onClick={revoke} data-testid="av-revoke-confirm" className="bg-red-700 hover:bg-red-800 text-white">Revoke</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Th({ children }) { return <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold">{children}</th>; }

function CreateVoucherDialog({ open, onClose, amenities, onCreated }) {
  const [amenityId, setAmenityId] = useState("");
  const [notes, setNotes] = useState("");
  const [qty, setQty] = useState(1);
  const [dtype, setDtype] = useState("WAIVE_ALL");
  const [dvalue, setDvalue] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setAmenityId(""); setNotes(""); setQty(1); setDtype("WAIVE_ALL"); setDvalue(""); } }, [open]);

  const submit = async () => {
    const v = Number(dvalue);
    if (dtype === "PERCENT" && !(v > 0 && v <= 100)) return toast.error("Percentage must be between 1 and 100");
    if (dtype === "FIXED" && !(v > 0)) return toast.error("Enter a fixed discount amount greater than 0");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/amenity-vouchers", {
        amenity_id: amenityId, notes: notes.trim(), quantity: Math.max(1, Math.min(50, Number(qty) || 1)),
        discount_type: dtype, discount_value: dtype === "WAIVE_ALL" ? 0 : v,
      });
      toast.success(`${data.created} voucher${data.created > 1 ? "s" : ""} generated`);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to generate voucher");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#fbf8f1] max-w-lg" data-testid="av-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Generate Waiver Voucher</DialogTitle>
          <DialogDescription>By default a voucher waives 100% of the booking. You can instead give a percentage or fixed-amount discount; the applicant then pays the balance online. Codes are single-use and valid for 7 days.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <label className="block">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Restrict to amenity (optional)</div>
            <select className={`${inputCls} w-full`} value={amenityId} onChange={(e) => setAmenityId(e.target.value)} data-testid="av-create-amenity">
              <option value="">Any amenity</option>
              {amenities.map((a) => <option key={a.id} value={a.id}>{a.title} · {a.amenity_type}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Discount</div>
              <select className={`${inputCls} w-full`} value={dtype} onChange={(e) => { setDtype(e.target.value); setDvalue(""); }} data-testid="av-create-discount-type">
                <option value="WAIVE_ALL">100% waiver</option>
                <option value="PERCENT">Percentage (%)</option>
                <option value="FIXED">Fixed amount (₹)</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">{dtype === "PERCENT" ? "Percent off" : dtype === "FIXED" ? "Amount off (₹)" : "Value"}</div>
              <input type="number" min="0" step={dtype === "PERCENT" ? "1" : "0.01"} max={dtype === "PERCENT" ? 100 : undefined} disabled={dtype === "WAIVE_ALL"} className={`${inputCls} w-full disabled:bg-[#f5f1e8]`} value={dtype === "WAIVE_ALL" ? "" : dvalue} placeholder={dtype === "WAIVE_ALL" ? "Full booking" : dtype === "PERCENT" ? "e.g. 25" : "e.g. 1000"} onChange={(e) => setDvalue(e.target.value)} data-testid="av-create-discount-value" />
            </label>
          </div>
          <label className="block">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Purpose / Notes (visible to admin only)</div>
            <textarea rows={3} className={`${inputCls} w-full`} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} data-testid="av-create-notes" placeholder="e.g. NGO event · Blood donation drive · Alumni meet" />
          </label>
          <label className="block">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Quantity (1-50)</div>
            <input type="number" min="1" max="50" className={`${inputCls} w-full`} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="av-create-qty" />
          </label>
          <div className="bg-[#E7F3F1] border border-[#0D5C63]/25 rounded-md p-3 text-[11px] text-[#0D5C63] inline-flex items-start gap-2">
            <Info size={14} className="mt-0.5" />
            <div>Each generated code expires <b>7 days</b> from now. Codes are single-use and non-transferable once redeemed.</div>
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} data-testid="av-create-submit" className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 rounded-sm font-semibold inline-flex items-center gap-2">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Ticket size={14} /> Generate</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
